import { TraitIdentifier } from "@dimensional-engineering/dimensional-models";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Roboto_Mono } from "next/font/google";
import { TraitType } from "@dimensional-engineering/dimensional-models/lib/models/traits/details/TraitType";

import styles from "./TopTraitsDeltaView.module.css";
import elementsJsonData from "../../../../../assets/traitInfo/elements.json";

import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import { useModalContext } from "@/context/ModalContext";
import ElementModal from "@/components/traits/TraitsModals/Element/ElementModal";
import Element from "@/components/traits/element/Element/Element";
import { getUserScaleScore } from "@/components/traits/utils";
import { determineIfTopNDeltaHasChanged } from "@/components/assessments-new/assessmentUtils";
import { dimensionData } from "@/assets/traitInfo/dimensionData";

type Props = {
  topNDeltaData?:
    | {
        previousTraitIdentifiers: TraitIdentifier[];
        currentTraitIdentifiers: TraitIdentifier[];
      }
    | null
    | undefined;
  dimensionSlug: string;
};

const font = Roboto_Mono({ subsets: ["latin"] });

export default function TopTraitsDeltaView(props: Props) {
  const { setModalComponent } = useModalContext();

  const dimensionName = dimensionData.find(
    (d) =>
      d.slug === props.dimensionSlug || d.longFormSlug === props.dimensionSlug
  )?.title;

  const elementDiscovery = useSelector(
    (state: RootState) => state.user.elementsDiscovered
  );

  const opacity = useStaggeredOpacity(4);

  const mappedPrevious = props.topNDeltaData?.previousTraitIdentifiers.map(
    (trait) => {
      const element = elementDiscovery?.find((e) => e.scaleSlug === trait.slug);
      const elementJson = elementsJsonData.find((e) => e.slug === trait.slug);

      if (trait.type === TraitType.element && elementJson) {
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<ElementModal slug={trait.slug} />);
            }}
            style={{ cursor: "pointer" }}
            key={trait.slug}
            className={styles.blackedOut}
          >
            <Element
              slug={trait.slug}
              score={getUserScaleScore(
                element?.scaleScore?.percentScore,
                trait.slug
              )}
            />
          </div>
        );
      }
    }
  );

  const mappedCurrent = props.topNDeltaData?.currentTraitIdentifiers.map(
    (trait) => {
      const element = elementDiscovery?.find((e) => e.scaleSlug === trait.slug);
      const elementJson = elementsJsonData.find((e) => e.slug === trait.slug);

      if (trait.type === TraitType.element && elementJson) {
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<ElementModal slug={trait.slug} />);
            }}
            style={{ cursor: "pointer" }}
            key={trait.slug}
          >
            <Element
              slug={trait.slug}
              score={getUserScaleScore(
                element?.scaleScore?.percentScore,
                trait.slug
              )}
            />
          </div>
        );
      }
    }
  );

  const changedText = determineIfTopNDeltaHasChanged(
    props.topNDeltaData?.previousTraitIdentifiers,
    props.topNDeltaData?.currentTraitIdentifiers
  )
    ? "have"
    : "have not";

  return (
    <div className={styles.main}>
      <p style={{ opacity: opacity.step1 }} className={styles.topText}>
        CHANGES
      </p>
      <div style={{ opacity: opacity.step2 }} className={styles.parent}>
        <p className={`${font.className} ${styles.topValuesText}`}>
          YOUR TOP {dimensionName?.toUpperCase()} TODAY:
        </p>
        <div className={styles.wrapper}>{mappedCurrent}</div>
      </div>

      <div style={{ opacity: opacity.step3 }} className={styles.parent}>
        <p
          style={{ opacity: "0.3" }}
          className={`${font.className} ${styles.topValuesText}`}
        >
          YOUR TOP {dimensionName?.toUpperCase()} BEFORE:
        </p>
        <div className={styles.wrapper}>{mappedPrevious}</div>
      </div>
      <p style={{ opacity: opacity.step4 }} className={styles.bottomText}>
        Your top {dimensionName?.toLowerCase()} {changedText} changed since last
        time.
      </p>
    </div>
  );
}
